import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * Reusable confirmation dialog. Replaces window.confirm() while keeping the same call shape:
 *
 *   const confirmed = await confirmFn({ title: 'Delete?', message: '...' });
 *   if (!confirmed) return;
 *
 * To use, wrap the consumer with `useConfirmDialog()` which returns `{ confirmFn, ConfirmDialogElement }`.
 * Render `<ConfirmDialogElement />` once near the root of your component, then call `confirmFn(opts)`
 * at any code site that previously called `window.confirm(...)`.
 */
export function ConfirmDialog({
  open, onOpenChange,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  testId = 'confirm-dialog',
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-xs h-8" data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </Button>
          <Button
            onClick={() => { onConfirm(); onOpenChange(false); }}
            className={`rounded-lg text-xs h-8 ${destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : 'bg-primary text-primary-foreground'}`}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * React hook that returns an imperative `confirmFn(opts)` and the JSX element to mount once.
 *
 *   const { confirmFn, ConfirmDialogElement } = useConfirmDialog();
 *   <ConfirmDialogElement />
 *   ...
 *   if (await confirmFn({ title: 'Delete user?', message: 'This cannot be undone', destructive: true, confirmLabel: 'Delete' })) { ... }
 */
export function useConfirmDialog() {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolverRef = useRef(null);
  const mountedRef = useRef(false);

  const confirmFn = useCallback((opts) => {
    if (process.env.NODE_ENV !== 'production' && !mountedRef.current) {
       
      console.warn(
        '[useConfirmDialog] confirmFn() was called before <ConfirmDialogElement /> was mounted. ' +
        'The returned promise will hang indefinitely. Make sure to render <ConfirmDialogElement /> ' +
        'inside *every* return path of your component (including early returns).'
      );
    }
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setState({ open: true, opts: opts || {} });
    });
  }, []);

  const handleClose = (open) => {
    if (!open && resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    setState(s => ({ ...s, open }));
  };

  const handleConfirm = () => {
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  };

  // Tiny inner component so the mount-tracker useEffect can live at the top of a real component.
  const ConfirmDialogElement = useCallback(() => (
    <ConfirmDialogElementInner
      mountedRef={mountedRef}
      open={state.open}
      opts={state.opts}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  ), [state.open, state.opts]);  

  return { confirmFn, ConfirmDialogElement };
}

function ConfirmDialogElementInner({ mountedRef, open, opts, onClose, onConfirm }) {
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, [mountedRef]);
  return <ConfirmDialog open={open} onOpenChange={onClose} onConfirm={onConfirm} {...opts} />;
}
